import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Avatar,
  Divider,
  IconButton,
  Tooltip
} from "@mui/material";
import { GroupAdd, People, MailOutline, Check, Close, Send, DeleteOutline } from "@mui/icons-material";
import axios from "axios";
import { useSnackbar } from "../context/Snackbar";

const TeamManagementPage = () => {
  const { showSuccess, showError, showInfo } = useSnackbar(); 
  const [email, setEmail] = useState("");
  const [team, setTeam] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [sentInvites, setSentInvites] = useState([]);

  const [loadingTeam, setLoadingTeam] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [loadingSentInvites, setLoadingSentInvites] = useState(true);

  const [isInviting, setIsInviting] = useState(false);
  const API_BASE = import.meta.env.VITE_API_BASE;

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoadingTeam(true);
      const response = await axios.post(`${API_BASE}/get_team_members`, {}, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      if (response.data.success) {
        setTeam(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching team members:", err);
      showError("Failed to load team members.");
    } finally {
      setLoadingTeam(false);
    }
  }, [API_BASE, showError]);

  const fetchPendingInvites = useCallback(async () => {
    try {
      setLoadingInvites(true);
      const response = await axios.post(`${API_BASE}/get-pending-invitations`, {}, {
        withCredentials: true
      });
      if (response.data.success) {
        setPendingInvites(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching pending invites:", err);
    } finally {
      setLoadingInvites(false);
    }
  }, [API_BASE]);

  const fetchSentInvites = useCallback(async () => {
    try {
      setLoadingSentInvites(true);
      const response = await axios.post(`${API_BASE}/get-sent-invitations`, {}, {
        withCredentials: true
      });
      if (response.data.success) {
        setSentInvites(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching sent invites:", err);
    } finally {
      setLoadingSentInvites(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchTeamMembers();
    fetchPendingInvites();
    fetchSentInvites();
  }, [fetchTeamMembers, fetchPendingInvites, fetchSentInvites]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setIsInviting(true);
    showInfo("Sending invitation...");

    try {
      const response = await axios.post(`${API_BASE}/invite_team_member`, { email }, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      showSuccess(response.data.message);
      setEmail("");
      fetchSentInvites();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to send invitation.";
      showError(errorMessage);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRespondInvite = async (inviteId, action) => {
    try {
      const response = await axios.post(`${API_BASE}/respond-invite`, { invite_id: inviteId, action }, {
        withCredentials: true
      });
      if (response.data.success) {
        showSuccess(response.data.message);
        fetchPendingInvites();
        if (action === 'accept') {
          setTimeout(() => { window.location.href = "/"; }, 1000);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || `Failed to ${action} invite.`;
      showError(errorMessage);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await axios.post(`${API_BASE}/cancel-invite`, { invite_id: inviteId }, {
        withCredentials: true
      });
      if (response.data.success) {
        showSuccess(response.data.message);
        fetchSentInvites();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to cancel invitation.";
      showError(errorMessage);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
        Team Management
      </Typography>

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4, borderLeft: '5px solid #1976d2' }} elevation={3}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ display: "flex", alignItems: "center", color: '#1976d2' }}>
          <MailOutline sx={{ mr: 1 }} /> Pending Invitations For You
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You have been invited to join the following teams. Accepting an invite will make you part of their workspace.
        </Typography>

        {loadingInvites ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress />
          </Box>
        ) : pendingInvites.length > 0 ? (
          <List>
            {pendingInvites.map((invite) => (
              <React.Fragment key={invite.id}>
                <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <ListItemText
                    primary={<Typography fontWeight="bold">{invite.sender_name} ({invite.sender_email})</Typography>}
                    secondary={`Invited you on ${new Date(invite.created_at).toLocaleDateString()}`}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<Check />}
                      onClick={() => handleRespondInvite(invite.id, 'accept')}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Close />}
                      onClick={() => handleRespondInvite(invite.id, 'reject')}
                    >
                      Reject
                    </Button>
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography sx={{ fontStyle: "italic", color: "text.secondary", p: 2 }}>
            You have no pending invitations.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }} elevation={3}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <GroupAdd sx={{ mr: 1 }} /> Invite a Team Member
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Send an invitation to someone to join your team. They will see the invite in their Team Management tab.
        </Typography>
        <Box
          component="form"
          onSubmit={handleInvite}
          sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}
        >
          <TextField
            type="email"
            label="Member's Email Address"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            disabled={isInviting}
          />
          <Button type="submit" variant="contained" disabled={isInviting || !email}>
            {isInviting ? <CircularProgress size={24} /> : "Send Invite"}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }} elevation={3}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <Send sx={{ mr: 1 }} fontSize="small" /> Invitations Sent By You
        </Typography>

        {loadingSentInvites ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress />
          </Box>
        ) : sentInvites.length > 0 ? (
          <List>
            {sentInvites.map((invite) => (
              <React.Fragment key={invite.id}>
                <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <ListItemText
                    primary={invite.invitee_email}
                    secondary={`Sent on ${new Date(invite.created_at).toLocaleDateString()} (Expires: ${new Date(invite.expires_at).toLocaleDateString()})`}
                  />
                  <Box sx={{ mt: { xs: 1, sm: 0 } }}>
                    <Tooltip title="Cancel Invitation">
                      <IconButton
                        color="error"
                        onClick={() => handleCancelInvite(invite.id)}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography sx={{ fontStyle: "italic", color: "text.secondary", p: 2 }}>
            You haven't sent any invitations yet.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={3}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <People sx={{ mr: 1 }} /> Your Team
        </Typography>

        {loadingTeam ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {team.length > 0 ? (
              team.map((member) => (
                <ListItem key={member.id} divider>
                  <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                    {member.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <ListItemText
                    primary={member.username}
                    secondary={`Email: ${member.email} | Joined: ${new Date(member.created_at).toLocaleDateString()}`}
                  />
                </ListItem>
              ))
            ) : (
              <Typography sx={{ fontStyle: "italic", color: "text.secondary", p: 2 }}>
                You haven't added any team members yet.
              </Typography>
            )}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default TeamManagementPage;